/**
 * Per-user, per-screen preferences — the LIST layout, the EDITOR layout, the
 * last TAB of each transaction screen, and WHERE its list layout switch
 * (`switch`), its editor layout switch (`editorSwitch`) and its editor's
 * Maximise / Restore (`sizeControl`) sit: on the screen, or in the ⋮ menu. Stored on the SERVER (the
 * user-memory store's `__screens__` record — `GET …/user-memory/screens` and
 * `PUT …/user-memory/screens/<obj>` on every host) so they follow the person
 * to another device, and mirrored to localStorage so the first paint already
 * wears the person's choice and an offline device keeps it.
 *
 * Same pattern as the shell's `shell/nav/prefs.ts`. The server normalises with
 * the same rules (`screen_prefs.py` / `ScreenPrefs.java`); ids come from
 * `listScreenConfig.json`.
 *
 * One-time migration: the list view used to be remembered in the browser
 * alone (`twasta.txn.view.<project>.<obj>`). A remembered `simple` / `document`
 * was really an EDITOR layout, so it becomes `view: list` + that editor; any
 * other value carries over as the view.
 */
import { useCallback, useEffect, useState } from 'react'
import {
  LEGACY_EDITOR_VIEWS, SCREEN_PREF_LIMITS, isEditorLayout, isLayoutSwitchPlace, isListLayout,
  type EditorLayout, type LayoutSwitchPlace,
} from '../list/listScreenConfig'

export interface ScreenPref {
  view?: string
  editor?: EditorLayout
  tab?: string
  switch?: LayoutSwitchPlace
  editorSwitch?: LayoutSwitchPlace
  sizeControl?: LayoutSwitchPlace
  at?: number
}

export interface ScreenPrefsDoc {
  v: 1
  screens: Record<string, ScreenPref>
  updated_at?: number
}

export type ScreenPrefPatch = {
  view?: string | null; editor?: EditorLayout | null; tab?: string | null
  switch?: LayoutSwitchPlace | null
  editorSwitch?: LayoutSwitchPlace | null
  sizeControl?: LayoutSwitchPlace | null
}

/** The keys whose value is a place (`bar` / `menu`) — the engines' PLACE_KEYS. */
const PLACE_KEYS = ['switch', 'editorSwitch', 'sizeControl'] as const

/** How a host reads and writes the server record. Either may reject; the
 *  screen then works on the local mirror alone. */
export interface ScreenPrefsTransport {
  load(): Promise<Partial<ScreenPrefsDoc> | null>
  save(objName: string, patch: ScreenPrefPatch): Promise<Partial<ScreenPrefsDoc> | null | void>
}

export const EMPTY_SCREEN_PREFS: ScreenPrefsDoc = { v: 1, screens: {} }

/** One screen's entry, clean ({} when nothing usable). */
export function normaliseScreen(raw: unknown): ScreenPref {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {}
  const r = raw as Record<string, unknown>
  const out: ScreenPref = {}
  let view = typeof r.view === 'string' ? r.view.trim() : ''
  let editor = typeof r.editor === 'string' ? r.editor.trim() : ''
  if (view in LEGACY_EDITOR_VIEWS) {
    editor = editor || LEGACY_EDITOR_VIEWS[view]
    view = 'list'
  }
  if (isListLayout(view)) out.view = view
  if (isEditorLayout(editor)) out.editor = editor
  for (const k of PLACE_KEYS) {
    const v = r[k]
    const place = typeof v === 'string' ? v.trim() : ''
    if (isLayoutSwitchPlace(place)) out[k] = place
  }
  let tab: unknown = r.tab
  if (typeof tab === 'number' && Number.isFinite(tab)) tab = String(tab)
  const t = typeof tab === 'string' ? tab.trim() : ''
  if (t && t.length <= SCREEN_PREF_LIMITS.maxTabIdLength) out.tab = t
  if (!Object.keys(out).length) return {}
  out.at = typeof r.at === 'number' && Number.isFinite(r.at) && r.at >= 0 ? Math.round(r.at) : 0
  return out
}

/** A clean document from anything (a server answer, a stale mirror, junk). Never throws. */
export function normaliseScreenPrefs(raw: unknown): ScreenPrefsDoc {
  const r = (raw && typeof raw === 'object') ? raw as Record<string, unknown> : {}
  const src = (r.screens && typeof r.screens === 'object' && !Array.isArray(r.screens))
    ? r.screens as Record<string, unknown> : {}
  const screens: Record<string, ScreenPref> = {}
  for (const [name, val] of Object.entries(src)) {
    const key = name.trim()
    if (!key || key.length > SCREEN_PREF_LIMITS.maxObjNameLength || key in screens) continue
    const e = normaliseScreen(val)
    if (Object.keys(e).length) screens[key] = e
  }
  const names = Object.keys(screens)
  if (names.length > SCREEN_PREF_LIMITS.maxScreens) {
    const keep = new Set(names
      .map((n, i) => ({ n, i, at: screens[n].at || 0 }))
      .sort((a, b) => (b.at - a.at) || (a.i - b.i))
      .slice(0, SCREEN_PREF_LIMITS.maxScreens).map((x) => x.n))
    for (const n of names) if (!keep.has(n)) delete screens[n]
  }
  return {
    v: 1, screens,
    updated_at: typeof r.updated_at === 'number' ? r.updated_at : undefined,
  }
}

/** `doc` with one screen changed by `patch` (null / '' clears a key). */
export function applyScreenPatch(doc: ScreenPrefsDoc, objName: string, patch: ScreenPrefPatch, at = Date.now()): ScreenPrefsDoc {
  const cur: Record<string, unknown> = { ...(doc.screens[objName] || {}) }
  delete cur.at
  for (const k of ['view', 'editor', 'tab', ...PLACE_KEYS] as const) {
    if (!(k in patch)) continue
    const v = patch[k]
    if (v == null || (typeof v === 'string' && !v.trim())) delete cur[k]
    else cur[k] = v
  }
  const screens = { ...doc.screens }
  delete screens[objName]
  const next = normaliseScreen({ ...cur, at })
  if (Object.keys(next).length) screens[objName] = next
  return normaliseScreenPrefs({ screens, updated_at: at })
}

// ── localStorage mirror + the old per-screen view key ────────────────────────

export function screenMirrorKey(project: string): string {
  return `twasta:screens:${project}`
}

export function readScreenMirror(project: string): ScreenPrefsDoc | null {
  try {
    const raw = window.localStorage.getItem(screenMirrorKey(project))
    return raw ? normaliseScreenPrefs(JSON.parse(raw)) : null
  } catch {
    return null
  }
}

export function writeScreenMirror(project: string, doc: ScreenPrefsDoc): void {
  try {
    window.localStorage.setItem(screenMirrorKey(project), JSON.stringify(doc))
  } catch {
    /* private mode / quota: the server copy still holds it */
  }
}

/** The pre-server, browser-only list view memory (TxnScreen used to write it). */
export const LEGACY_VIEW_KEY = 'twasta.txn.view.'

/** What the old browser-only key held for this screen, as a patch — and the
 *  key is removed so the migration runs once. Null when there is nothing. */
export function takeLegacyView(project: string, objName: string): ScreenPrefPatch | null {
  try {
    const key = `${LEGACY_VIEW_KEY}${project}.${objName}`
    const raw = window.localStorage.getItem(key)
    if (raw == null) return null
    window.localStorage.removeItem(key)
    const v = raw.trim()
    if (v in LEGACY_EDITOR_VIEWS) return { view: 'list', editor: LEGACY_EDITOR_VIEWS[v] as EditorLayout }
    return isListLayout(v) ? { view: v } : null
  } catch {
    return null
  }
}

// ── the shared per-application store + hook ──────────────────────────────────
//
// Read ONCE per application (every screen of the app shares the answer) and
// written through on change — debounced per screen, so clicking through a few
// tabs sends one request. The mirror is the first answer and the offline one.

interface AppStore {
  doc: ScreenPrefsDoc
  ready: boolean
  loading: Promise<void> | null
  transport: ScreenPrefsTransport | null
  listeners: Set<() => void>
  // Changes not yet sent, per screen. Recorded even before a transport is
  // bound (a choice made while the server is still answering), and sent once
  // it is; `timer` is null until then.
  pending: Map<string, { patch: ScreenPrefPatch; timer: ReturnType<typeof setTimeout> | null }>
}

const STORES = new Map<string, AppStore>()
const SAVE_DEBOUNCE_MS = 400

function storeFor(project: string): AppStore {
  let st = STORES.get(project)
  if (!st) {
    st = {
      doc: readScreenMirror(project) || EMPTY_SCREEN_PREFS,
      ready: false, loading: null, transport: null,
      listeners: new Set(), pending: new Map(),
    }
    STORES.set(project, st)
  }
  return st
}

function emit(st: AppStore): void {
  for (const l of [...st.listeners]) {
    try { l() } catch { /* a listener's failure must not stop the others */ }
  }
}

function ensureLoaded(project: string, st: AppStore, transport: ScreenPrefsTransport | null | undefined): void {
  if (!transport) { st.ready = true; return }
  st.transport = transport
  if (st.loading || st.ready) return
  st.loading = transport.load().then((raw) => {
    if (raw) {
      // The server holds the person's choices across devices — it wins over
      // this browser's mirror, except for screens with a write still queued.
      const server = normaliseScreenPrefs(raw)
      const screens = { ...server.screens }
      for (const name of st.pending.keys()) {
        if (st.doc.screens[name]) screens[name] = st.doc.screens[name]
      }
      st.doc = normaliseScreenPrefs({ screens, updated_at: server.updated_at })
      writeScreenMirror(project, st.doc)
    }
  }).catch((err) => {
    // Unreachable / refused: keep working on the mirror alone.
    console.warn('[screenPrefs] could not read screen preferences; using this browser\'s copy', err)
  }).finally(() => {
    st.ready = true
    st.loading = null
    // Send what was chosen while the answer was on its way.
    for (const [name, p] of [...st.pending]) {
      if (p.timer == null) scheduleSave(st, name, {})
    }
    emit(st)
  })
}

function scheduleSave(st: AppStore, objName: string, patch: ScreenPrefPatch): void {
  const prev = st.pending.get(objName)
  if (prev?.timer) clearTimeout(prev.timer)
  const merged: ScreenPrefPatch = { ...(prev?.patch || {}), ...patch }
  const t = st.transport
  // Bound but still loading, or not bound: hold it (sent after the load).
  if (!t || st.loading) {
    st.pending.set(objName, { patch: merged, timer: null })
    return
  }
  const timer = setTimeout(() => {
    st.pending.delete(objName)
    t.save(objName, merged).catch((err) => {
      console.warn(`[screenPrefs] could not save preferences for ${objName}; kept in this browser`, err)
    })
  }, SAVE_DEBOUNCE_MS)
  st.pending.set(objName, { patch: merged, timer })
}

/** Change one screen's preferences: the mirror and every mounted screen at
 *  once, the server shortly after. */
export function updateScreenPref(project: string, objName: string, patch: ScreenPrefPatch): void {
  if (!project || !objName) return
  const st = storeFor(project)
  st.doc = applyScreenPatch(st.doc, objName, patch)
  writeScreenMirror(project, st.doc)
  emit(st)
  scheduleSave(st, objName, patch)
}

/** Bind an application's transport and read the server record once (what
 *  `useScreenPref` does on mount). Resolves when the answer is in. */
export function loadScreenPrefs(project: string, transport: ScreenPrefsTransport | null | undefined): Promise<void> {
  const st = storeFor(project)
  ensureLoaded(project, st, transport)
  return st.loading || Promise.resolve()
}

/** Test seam: forget every application's store. */
export function resetScreenPrefStores(): void {
  for (const st of STORES.values()) for (const p of st.pending.values()) if (p.timer) clearTimeout(p.timer)
  STORES.clear()
}

export function getScreenPref(project: string, objName: string): ScreenPref {
  return storeFor(project).doc.screens[objName] || {}
}

/**
 * One screen's remembered preferences. `pref` is the mirror's answer at once
 * and the server's once it has answered (`ready`); `update` changes it
 * everywhere. Without a transport (a designer preview, an embed) it is the
 * mirror alone. Runs the one-time migration of the old browser-only view key.
 */
export function useScreenPref(
  project: string, objName: string, transport?: ScreenPrefsTransport | null,
): { pref: ScreenPref; ready: boolean; update: (patch: ScreenPrefPatch) => void } {
  const st = storeFor(project)
  const [, bump] = useState(0)
  useEffect(() => {
    const l = () => bump((n) => n + 1)
    st.listeners.add(l)
    ensureLoaded(project, st, transport)
    return () => { st.listeners.delete(l) }
  }, [project, st, transport])
  useEffect(() => {
    const legacy = takeLegacyView(project, objName)
    if (legacy && !st.doc.screens[objName]) updateScreenPref(project, objName, legacy)
  }, [project, objName, st])
  const update = useCallback(
    (patch: ScreenPrefPatch) => updateScreenPref(project, objName, patch),
    [project, objName],
  )
  return { pref: st.doc.screens[objName] || {}, ready: st.ready, update }
}
